import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { Card, IconCard, List, TextCard, Icon, Badge, Column, Video } from '@pella/sash-framework';
import { classNames } from '@pella/sash-framework/dist/utilities';
import { CTA } from '../../';

const CardRenderer = ({ data, ...other }) => {
	if (!data?.card_type) return null;

	// CTA List Classes for 'standard' card type
	const standardListClasses = classNames({
		'space-y-2': true,
		'pt-4': !data.variant || data.variant === 'standard-padded',
		'px-4 pb-4': data.variant === 'standard-padded-content',
		'mt-auto': ((!data.variant || data.variant === 'standard-padded') && data.image?.[0]) || data.variant === 'standard-full' || data.variant === 'standard-padded-content',
	});

	const promoCardClasses = classNames({
		'sash-Card--promo !py-6 !px-4 md:!p-3 grid grid-rows-[1fr_auto_auto_1fr] xl:auto-rows-min grid-cols-[75px_1fr] gap-x-6 md:flex md:flex-col': true,
		'!border-2 !border-black': data.variant === 'all-black',
	});

	switch (data.card_type) {
		case 'icon':
			return (
				<IconCard
					variant={data.variant}
					{...other}
				>
					{data.cta?.length === 1 && (
						<Link
							to={data.cta[0]?.destination?.[0]?.destination}
							className='cardLink absolute right-0 top-0 !block h-full !w-full -indent-full'
						>
							{data.seo_tag || data.heading}
						</Link>
					)}

					{data.material_symbol && <IconCard.Icon name={data.material_symbol} />}
					{data.image?.[0]?.file?.url && <IconCard.Image src={data.image[0].file?.url + '?auto=webp&quality=90'} />}
					{data.heading && <IconCard.Heading content={data.heading} />}
					<IconCard.Content
						content={data.description}
						className='!mt-2'
					>
						{data.cta?.[0] && (data.variant != null || data.cta.length > 1) && (
							<List
								bullets='none'
								className='mt-auto pt-4'
							>
								{data.cta.map((cta, index) => (
									<li key={index}>
										<CTA
											data={cta}
											className={`relative ${!cta.button_style || cta.button_style === 'tertiary' ? 'primary-link !text-base' : ''}`}
										/>
									</li>
								))}
							</List>
						)}
					</IconCard.Content>
				</IconCard>
			);

		case 'box-card':
			return (
				<TextCard
					variant={data.variant}
					{...other}
				>
					{data.cta?.length === 1 && (
						<Link
							to={data.cta[0]?.destination?.[0]?.destination}
							className='cardLink absolute right-0 top-0 !block h-full !w-full -indent-full'
						>
							{data.seo_tag || data.heading}
						</Link>
					)}

					{data.heading && (
						<TextCard.Heading content={data.heading}>
							{data.image?.[0]?.file?.url && <TextCard.Image src={data.image[0].file?.url + '?auto=webp&quality=90'} />}
							{data.material_symbol && <TextCard.Icon name={data.material_symbol} />}
						</TextCard.Heading>
					)}
					<TextCard.Content content={data.description}>
						{data.cta?.[0] && data.cta.length > 1 && (
							<List bullets='none'>
								{data.cta.map((cta, index) => (
									<li key={index}>
										<CTA
											data={cta}
											className={`relative ${!cta.button_style || cta.button_style === 'tertiary' ? 'primary-link !font-normal normal-case' : ''}`}
										/>
									</li>
								))}
							</List>
						)}
					</TextCard.Content>
				</TextCard>
			);

		case 'promo':
			return (
				<Card
					className={promoCardClasses}
					{...other}
				>
					{data.cta?.length === 1 && (
						<Link
							to={data.cta[0]?.destination?.[0]?.destination}
							className='cardLink absolute right-0 top-0 !block h-full !w-full -indent-full'
						>
							{data.seo_tag || data.heading}
						</Link>
					)}

					{data.badge?.material_symbol && (
						<Badge
							variant='pill'
							style={{ backgroundColor: data.badge?.badge_hex_value, color: data.badge?.text_hex_value }}
							className='badge-bg col-start-2 row-start-2 w-fit self-start border-none !px-2 !py-[5px] md:absolute md:left-3 md:top-3'
						>
							{data.badge.material_symbol && (
								<Icon
									size={12}
									className='mr-1'
									name={data.badge.material_symbol}
								/>
							)}
							{data.badge.heading && <p className='text-xs !leading-none text-white'>{data.badge.heading}</p>}
						</Badge>
					)}

					{data.image?.[0]?.file?.url && (
						<Card.Image
							src={data.image[0].file?.url + '?auto=webp&quality=90'}
							className='col-start-1 row-span-4 row-start-1 !mb-0 !aspect-auto h-[148px] self-center !object-contain md:col-start-1 md:row-span-1 md:row-start-1 md:!mb-4 md:mt-auto md:h-[117px] xl:h-[148px]'
						/>
					)}

					{data.heading && (
						<Card.Heading
							content={data.heading}
							className='col-start-2 row-start-3 mt-2 max-w-[12ch] text-xl font-semibold capitalize !leading-tight tracking-0.5px !text-black md:col-start-1 md:row-start-2 md:mx-auto md:mb-auto md:mt-0 md:text-center md:text-2xl xl:text-26px'
						/>
					)}

					{data.description && <Card.Content content={data.description} />}
				</Card>
			);

		default:
			return (
				<Card
					variant={data.variant}
					{...other}
				>
					{data.cta?.length === 1 && (
						<Link
							to={data.cta[0]?.destination?.[0]?.destination}
							className='cardLink absolute right-0 top-0 !block h-full !w-full -indent-full'
						>
							{data.seo_tag || data.heading}
						</Link>
					)}

					{data.image?.[0]?.file?.url && <Card.Image src={data.image[0].file?.url + '?auto=webp&quality=90'} />}

					{data.video?.[0] && (
						<Video
							source={data.video[0].video_id || data.video[0].internal_video?.url}
							title={data.video[0].video_title}
							poster={data.video[0].image_overlay?.[0]?.file?.url ? `${data.video[0].image_overlay[0].file.url}?format=pjpg&auto=webp&quality=90` : undefined}
							transcript={data.video[0].transcript}
						/>
					)}

					<div className='flex-none'>
						{data.badge && data.badge.material_symbol && (
							<Badge
								variant='pill'
								style={{ backgroundColor: data.badge?.badge_hex_value, color: data.badge?.text_hex_value }}
								className='badge-bg mb-1 border-none !px-2 !py-[5px]'
							>
								{data.badge?.material_symbol && (
									<Icon
										size={16}
										className='mr-1'
										name={data.badge.material_symbol}
									/>
								)}
								{data.badge?.heading && (
									<Card.Heading
										className='!text-xs font-semibold md:text-base'
										content={data.badge.heading}
									/>
								)}
							</Badge>
						)}
					</div>

					{data.product_line && <p className='mb-0.5 text-sm font-semibold uppercase leading-none tracking-2px'>{data.product_line}</p>}
					{data.heading && (
						<Card.Heading
							content={data.heading}
							className='!text-sm !font-normal !leading-tight'
						/>
					)}
					{data.impact_non && <p className='mt-0.5 text-xs font-bold uppercase tracking-widest'>{data.impact_non}</p>}

					<Card.Content content={data.description} />

					{data.cta?.[0] && (
						<List
							bullets='none'
							className={standardListClasses}
						>
							{data.cta.map((cta, index) => (
								<li key={index}>
									<CTA
										data={cta}
										className={!cta.button_style || cta.button_style === 'tertiary' ? `primary-link relative !font-normal normal-case` : null}
									/>
								</li>
							))}
						</List>
					)}
				</Card>
			);
	}
};

CardRenderer.propTypes = {
	data: PropTypes.object,
};

export default CardRenderer;
